import React, { useLayoutEffect, useMemo, useRef } from "react";
import { invalidate } from "@react-three/fiber";
import { useSelector } from "react-redux";
import * as THREE from "three";
import { formatHeatmapValue } from "../utils";
import { DEFAULT_HEATMAP_LABEL_DIGITS } from "./heatmapLabelLayers";
import {
  computeSpriteLabelScale,
  getTriangulatedFaceArea,
  getTriangulatedFaceCentroid,
} from "./heatmapLabelSizing";

const DEFAULT_NORMAL_OFFSET = 0.02;
const LOCAL_UP = new THREE.Vector3(0, 0, 1);
const FALLBACK_RIGHT = new THREE.Vector3(1, 0, 0);

function getFaceNormal(polygon) {
  if (!Array.isArray(polygon) || polygon.length < 9) {
    return new THREE.Vector3(0, 0, 1);
  }

  const ax = polygon[0];
  const ay = polygon[1];
  const az = polygon[2];
  const bx = polygon[3];
  const by = polygon[4];
  const bz = polygon[5];
  const cx = polygon[6];
  const cy = polygon[7];
  const cz = polygon[8];

  const normal = new THREE.Vector3(
    (by - ay) * (cz - az) - (bz - az) * (cy - ay),
    (bz - az) * (cx - ax) - (bx - ax) * (cz - az),
    (bx - ax) * (cy - ay) - (by - ay) * (cx - ax),
  );

  if (normal.lengthSq() < 1e-12) return new THREE.Vector3(0, 0, 1);
  normal.normalize();
  if (normal.z < 0) normal.negate();
  return normal;
}

function createLabelTexture(text) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const fontSize = 64;
  const font = `700 ${fontSize}px system-ui, sans-serif`;
  ctx.font = font;
  const width = Math.ceil(ctx.measureText(text).width + 28);
  const height = Math.ceil(fontSize * 1.35 + 20);
  canvas.width = Math.max(32, width);
  canvas.height = Math.max(32, height);

  ctx.font = font;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  ctx.lineWidth = Math.max(4, fontSize * 0.16);
  ctx.strokeStyle = "rgba(255,255,255,0.95)";
  ctx.fillStyle = "#111111";
  const x = canvas.width / 2;
  const y = canvas.height / 2;
  ctx.strokeText(text, x, y);
  ctx.fillText(text, x, y);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return {
    texture,
    aspect: canvas.width / canvas.height,
  };
}

function orientLabelToFace(mesh, normal) {
  const zAxis = normal.clone().normalize();
  const xAxis = new THREE.Vector3().crossVectors(LOCAL_UP, zAxis);
  if (xAxis.lengthSq() < 1e-8) {
    xAxis.copy(FALLBACK_RIGHT).cross(zAxis);
  }
  xAxis.normalize();
  const yAxis = new THREE.Vector3().crossVectors(zAxis, xAxis).normalize();
  mesh.quaternion.setFromRotationMatrix(
    new THREE.Matrix4().makeBasis(xAxis, yAxis, zAxis),
  );
}

function disposeGroupContents(group, sharedGeometry) {
  const maps = new Set();
  const materials = new Set();
  const children = [...group.children];
  children.forEach((child) => {
    group.remove(child);
    if (child.material) {
      materials.add(child.material);
      if (child.material.map) maps.add(child.material.map);
    }
  });
  materials.forEach((material) => material.dispose());
  maps.forEach((map) => map.dispose());
  if (sharedGeometry) sharedGeometry.dispose();
}

export function SurfaceAlignedValueLabels({
  items = [],
  getPosition,
  getValue,
  getVisible,
  getLabelPolygon,
  visible = true,
  fontScale = 1,
  normalOffset = DEFAULT_NORMAL_OFFSET,
  updateKey,
  layerKey,
}) {
  const groupRef = useRef();
  const configRef = useRef({
    getPosition,
    getValue,
    getVisible,
    getLabelPolygon,
  });
  configRef.current = {
    getPosition,
    getValue,
    getVisible,
    getLabelPolygon,
  };

  const labelsEnabled = useSelector(
    (state) => state.sceneVar.heatmapLabelsEnabled,
  );
  const adaptive = useSelector((state) => state.sceneVar.heatmapLabelsAdaptive);
  const sizePercent = useSelector(
    (state) => state.sceneVar.heatmapLabelsSizePercent,
  );
  const digits = useSelector(
    (state) =>
      state.sceneVar.heatmapLabelDigitsByLayer?.[layerKey] ??
      DEFAULT_HEATMAP_LABEL_DIGITS,
  );

  const labelData = useMemo(() => {
    if (!visible || !labelsEnabled || !items.length) return [];

    const {
      getPosition: readPosition,
      getValue: readValue,
      getVisible: readVisible,
      getLabelPolygon: readPolygon,
    } = configRef.current;

    const next = [];
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (readVisible && !readVisible(item)) continue;

      const text = formatHeatmapValue(readValue(item), digits);
      if (!text) continue;

      const polygon = readPolygon?.(item);
      const normal = getFaceNormal(polygon);
      const faceCentroid = getTriangulatedFaceCentroid(polygon);
      const [x, y, z] = faceCentroid || readPosition(item);
      next.push({
        text,
        position: [
          x + normal.x * normalOffset,
          y + normal.y * normalOffset,
          z + normal.z * normalOffset,
        ],
        normal: [normal.x, normal.y, normal.z],
        area: getTriangulatedFaceArea(polygon),
      });
    }
    return next;
  }, [
    visible,
    labelsEnabled,
    items,
    updateKey,
    adaptive,
    sizePercent,
    digits,
    normalOffset,
  ]);

  useLayoutEffect(() => {
    const group = groupRef.current;
    if (!group) return undefined;

    let sharedGeometry = null;
    disposeGroupContents(group, null);
    if (!labelData.length) {
      invalidate();
      return undefined;
    }

    sharedGeometry = new THREE.PlaneGeometry(1, 1);
    const materialCache = new Map();
    const normal = new THREE.Vector3();

    for (let i = 0; i < labelData.length; i += 1) {
      const { text, position, normal: normalArr, area } = labelData[i];
      let cached = materialCache.get(text);
      if (!cached) {
        const { texture, aspect } = createLabelTexture(text);
        cached = {
          aspect,
          material: new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
            depthTest: true,
            depthWrite: false,
            side: THREE.DoubleSide,
            toneMapped: false,
            polygonOffset: true,
            polygonOffsetFactor: -8,
            polygonOffsetUnits: -8,
          }),
        };
        materialCache.set(text, cached);
      }

      const scale = computeSpriteLabelScale({
        adaptive,
        sizePercent,
        fontScale,
        area,
      });

      const mesh = new THREE.Mesh(sharedGeometry, cached.material);
      mesh.position.set(position[0], position[1], position[2]);
      mesh.scale.set(scale * cached.aspect, scale, 1);
      normal.set(normalArr[0], normalArr[1], normalArr[2]);
      orientLabelToFace(mesh, normal);
      mesh.renderOrder = 20;
      group.add(mesh);
    }

    invalidate();

    return () => {
      disposeGroupContents(group, sharedGeometry);
    };
  }, [labelData, fontScale, adaptive, sizePercent]);

  if (!visible || !labelsEnabled) return null;

  return <group ref={groupRef} />;
}

export const VerticalWindowValueLabels = SurfaceAlignedValueLabels;
